"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, ArrowRight, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  businessName: "TreppenGlanz",
  address: "Musterstraße 123, 12345 Musterstadt",
  phone: "+49 123 4567890",
  email: "kontakt@treppenglanz.de",
  hours: "Mo-Fr: 08:00 - 18:00 Uhr"
};
export default function AnfrageStellen() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="anfrage-stellen" ref={ref} className="relative w-full bg-background py-20 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute -left-[10%] top-[20%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -right-[10%] bottom-[20%] w-[40%] h-[40%] bg-accent/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
                Kontakt &amp; <span className="text-accent">Anfrage</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Sie haben Fragen zu unseren Leistungen oder wünschen ein
                unverbindliches Angebot? Wir sind für Sie da – zuverlässig,
                schnell und persönlich.
              </p>
            </div>

            <div className="space-y-8">
              {}
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/5 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-300">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Anschrift</h3>
                  <p className="text-muted-foreground mt-1">STW Servie Team Weingarten,<div>Pascal Spieß<br />Jakob-Reiner-Str. 12, 88250  Weingarten</div></p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/5 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-300">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Telefon</h3>
                  <p className="text-muted-foreground mt-1">
                    <Link to={`tel:${SHARED_DATA.phone}`} className="hover:text-accent transition-colors">+49 751 20183728</Link>
                  </p>
                  
                </div>
              </div>

              {}
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/5 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-300">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">E-Mail</h3>
                  <p className="text-muted-foreground mt-1">
                    <Link to={`mailto:${SHARED_DATA.email}`} className="hover:text-accent transition-colors">info@serviceteamweingarten.com</Link>
                  </p>
                  
                </div>
              </div>

              {}
              
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div className="bg-card rounded-2xl shadow-xl border border-border/50 p-6 sm:p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-bl-full -mr-16 -mt-16 pointer-events-none" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground">Anfrage stellen</h3>
                <p className="text-muted-foreground mt-2">
                  Füllen Sie das Formular aus und wir melden uns umgehend bei Ihnen.
                </p>
              </div>

              <FormProvider formId="request_form_contact" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: "Bitte geben Sie Ihren Namen ein."
              },
              email: {
                required: "E-Mail Adresse ist erforderlich.",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Bitte geben Sie eine gültige E-Mail Adresse ein."
                }
              },
              subject: {
                required: "Bitte geben Sie einen Betreff an."
              },
              message: {
                required: "Bitte geben Sie eine Nachricht ein."
              }
            }} submitText="Anfrage absenden" successMessage="Vielen Dank! Ihre Anfrage wurde erfolgreich versendet. Wir melden uns in Kürze." errorMessage="Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Anfrage versendet!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel>Ihr Name <span className="text-accent">*</span></FormLabel>
              <FormControl>
                <input className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-accent/50" placeholder="Max Mustermann" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel>E-Mail Adresse <span className="text-accent">*</span></FormLabel>
              <FormControl>
                <input type="email" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-accent/50" placeholder="max@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonnummer</FormLabel>
              <FormControl>
                <input type="tel" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-accent/50" placeholder="+49 123 4567890" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Optional – für Rückfragen.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Firma / Organisation</FormLabel>
              <FormControl>
                <input className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-accent/50" placeholder="Beispiel GmbH" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel>Betreff <span className="text-accent">*</span></FormLabel>
            <FormControl>
              <input className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-accent/50" placeholder="Anfrage zur Spezialreinigung" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Nachricht <span className="text-accent">*</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all duration-200 hover:border-accent/50" placeholder="Bitte schildern Sie Ihr Anliegen ausführlich." {...field} />
            </FormControl>
            <FormDescription className="text-xs">
              Je mehr Details, desto schneller können wir Ihnen ein Angebot erstellen.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/20 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Wird gesendet...
          </> : <>
            Anfrage absenden <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        Mit dem Absenden erklären Sie sich mit unserer Datenschutzerklärung einverstanden.
      </p>
    </Form>;
}